import React, { Suspense, lazy } from 'react';
import { Outlet } from 'react-router-dom';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ToastContainer, toast, Zoom } from 'react-toastify';

import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-tooltip/dist/react-tooltip.css';
import './assets/css/index.css';
import './assets/css/toast.css';

import Layout from './pages/Layout';

// Guest
const Login = lazy(() => import('./pages/Guest/Login'));
const Forgot = lazy(() => import('./pages/Guest/Forgot'));
const Reset = lazy(() => import('./pages/Guest/Reset'));

// Section
const DeviceLog = lazy(() => import('./pages/DeviceActivityLog/index'));
const ActivityLogList = lazy(() => import('./pages/ActivityLog/index'));
const CronLogList = lazy(() => import('./pages/Cron/index'));
const MediaList = lazy(() => import('./pages/Media/index'));


//User (Admin)
const AccountList = lazy(() => import('./pages/Account/index'));
const AccountForm = lazy(() => import('./pages/Account/Form'));
const ChangePassword = lazy(() => import('./pages/Account/ChangePassword'));

//Monk User (FE)
const UserAppList = lazy(() => import('./pages/UserApp/index'));
const UserAppForm = lazy(() => import('./pages/UserApp/Form'));
const UserAppChangePassword = lazy(() => import('./pages/UserApp/ChangePassword'));

//Programme
const ProgrammeList = lazy(() => import('./pages/Programme/index'));
const ProgrammeForm = lazy(() => import('./pages/Programme/Form'));

//Plunge
const PlungeList = lazy(() => import('./pages/Plunge/index'));
const PlungeForm = lazy(() => import('./pages/Plunge/Form'));

//Firmware
const FirmwareList = lazy(() => import('./pages/Firmware/index'));
const FirmwareForm = lazy(() => import('./pages/Firmware/Form'));

//Subscriber
const SubscriberList = lazy(() => import('./pages/Subscriber/index'));

const MyMonkList = lazy(() => import('./pages/MyMonk/index'));

//Contact
const ContactList = lazy(() => import('./pages/Contact/index'));
const ContactView = lazy(() => import('./pages/Contact/View'));

//Monk
const MonkList = lazy(() => import('./pages/Monk/index'));
const MonkView = lazy(() => import('./pages/Monk/View'));

//Monk Device
const MonkDeviceList = lazy(() => import('./pages/MonkDevice/index'));
const MonkDeviceForm = lazy(() => import('./pages/MonkDevice/Form'));

//Email Content
const EmailContentList = lazy(() => import('./pages/EmailContent/index'));
const EmailContentForm = lazy(() => import('./pages/EmailContent/Form'));

// Education
const EducationList = lazy(() => import('./pages/Education/index'));
const EducationForm = lazy(() => import('./pages/Education/Form'));

const DeviceDefaultList = lazy(() => import('./pages/DeviceDefault/index'));
const DeviceDefaultForm = lazy(() => import('./pages/DeviceDefault/Form'));

// Drop In Section
const DropInSessionList = lazy(() => import('./pages/DropInSession/index'));
const DropInSessionForm = lazy(() => import('./pages/DropInSession/Form'));

//Settings
const SettingsForm = lazy(() => import('./pages/Section/Settings'));

//Home
const HomeForm = lazy(() => import('./pages/Section/Home'));

//Error Content
const ErrorContentList = lazy(() => import('./pages/ErrorContent/index'));
const ErrorContentForm = lazy(() => import('./pages/ErrorContent/Form'));

const InitLayout = () => {
	return (<Outlet />)
}

const SuspendPage = (element, fallback = <div />) => {
	return (
		<Suspense fallback={fallback}>
			{element}
		</Suspense>
	)
}

function App() {
	return (
		<BrowserRouter basename='/'>
			<Routes>
				<Route element={<InitLayout />}>
					<Route path="/" element={SuspendPage(<Login />)} />
					<Route path="/forgot-password" element={SuspendPage(<Forgot />)} />
					<Route path="/reset-password/:hash" element={SuspendPage(<Reset />)} />

					<Route element={<Layout />}>
						<Route path="/media" element={SuspendPage(<MediaList />)} />
						<Route path="/activity" element={SuspendPage(<ActivityLogList />)} />
						<Route path="/deviceactivity" element={SuspendPage(<DeviceLog />)} />
						<Route path="/cronlog" element={SuspendPage(<CronLogList />)} />

						<Route path="/account" element={SuspendPage(<AccountList />)} />
						<Route path="/account/create" element={SuspendPage(<AccountForm />)} />
						<Route path="/account/:id" element={SuspendPage(<AccountForm />)} />
						<Route path="/password/:id" element={SuspendPage(<ChangePassword />)} />

						<Route path="/userapp" element={SuspendPage(<UserAppList />)} />
						<Route path="/userapp/create" element={SuspendPage(<UserAppForm />)} />
						<Route path="/userapp/:id" element={SuspendPage(<UserAppForm />)} />
						<Route path="/userapp/password/:id" element={SuspendPage(<UserAppChangePassword />)} />

						<Route path="/programme" element={SuspendPage(<ProgrammeList />)} />
						<Route path="/programme/create" element={SuspendPage(<ProgrammeForm />)} />
						<Route path="/programme/:id" element={SuspendPage(<ProgrammeForm />)} />

						<Route path="/programmeplunge" element={SuspendPage(<PlungeList />)} />
						<Route path="/programmeplunge/create" element={SuspendPage(<PlungeForm />)} />
						<Route path="/programmeplunge/:id" element={SuspendPage(<PlungeForm />)} />

						<Route path="/subscriber" element={SuspendPage(<SubscriberList />)} />

						<Route path="/mymonk" element={SuspendPage(<MyMonkList />)} />
						<Route path="/device" element={SuspendPage(<MyMonkList title={"Connected Devices"} />)} />

						<Route path="/contact/:id" element={SuspendPage(<ContactView />)} />
						<Route path="/contact" element={SuspendPage(<ContactList />)} />

						<Route path="/monkdevice" element={SuspendPage(<MonkDeviceList />)} />
						<Route path="/monkdevice/:id" element={SuspendPage(<MonkDeviceForm />)} />
						<Route path="/monkdevice/create" element={SuspendPage(<MonkDeviceForm />)} />

						<Route path="/firmware" element={SuspendPage(<FirmwareList />)} />
						<Route path="/firmware/:id" element={SuspendPage(<FirmwareForm />)} />
						<Route path="/firmware/create" element={SuspendPage(<FirmwareForm />)} />

						<Route path="/emailcontent" element={SuspendPage(<EmailContentList />)} />
						<Route path="/emailcontent/:id" element={SuspendPage(<EmailContentForm />)} />
						<Route path="/emailcontent/create" element={SuspendPage(<EmailContentForm />)} />

						<Route path="/education" element={SuspendPage(<EducationList />)} />
						<Route path="/education/create" element={SuspendPage(<EducationForm />)} />
						<Route path="/education/:id" element={SuspendPage(<EducationForm />)} />

						<Route path="/devicedefault" element={SuspendPage(<DeviceDefaultList />)} />
						<Route path="/devicedefault/create" element={SuspendPage(<DeviceDefaultForm />)} />
						<Route path="/devicedefault/:id" element={SuspendPage(<DeviceDefaultForm />)} />

						<Route path="/dropinsession" element={SuspendPage(<DropInSessionList />)} />
						<Route path="/dropinsession/create" element={SuspendPage(<DropInSessionForm />)} />
						<Route path="/dropinsession/:id" element={SuspendPage(<DropInSessionForm />)} />

						<Route path="/errorcontent" element={SuspendPage(<ErrorContentList />)} />
						<Route path="/errorcontent/:id" element={SuspendPage(<ErrorContentForm />)} />
						<Route path="/errorcontent/create" element={SuspendPage(<ErrorContentForm />)} />

						<Route path="/settings" element={SuspendPage(<SettingsForm />)} />
						<Route path="/home" element={SuspendPage(<HomeForm />)} />

					</Route>
				</Route>




			</Routes>
			<ToastContainer draggable={false} position={toast.POSITION.TOP_RIGHT} autoClose={6000} transition={Zoom} />
		</BrowserRouter>
	);
}

export default App;
